/**
 * THE STATUS WORDS, AS CODES (ease package P-5.1, P-5.2, P-5.6, P-5.9).
 *
 * A third spelling of `ai-sidecar/src/status/types.ts` (the BFF's `status-codes.ts` is the
 * second, and `services/authoring-web/test/status-parity.test.ts` proves the two agree). This
 * one is pinned from the fork side by `specs/status.spec.js`, as literals. Every sentence an
 * owner reads for these codes is in `i18n.js` under `WASLO.STATUS`, `WASLO.READINESS` and
 * `WASLO.ANSWERS.REASON` / `NOTE`; nothing here is copy.
 */

/** Every state a channel can be in. The account's are the same list less the two channel-only ones. */
export const STATUS_STATES = Object.freeze([
  'not_set_up',
  'not_connected',
  'channel_off',
  'paused_by_us',
  'platform_limit',
  'out_of_credits',
  'month_limit',
  'day_limit',
  'switched_off',
  'practice',
  'answering',
]);

export const ACCOUNT_STATES = Object.freeze(
  STATUS_STATES.filter(
    state => state !== 'not_connected' && state !== 'channel_off'
  )
);

export const STATUS_FIXES = Object.freeze([
  'none',
  'request_connect',
  'request_top_up',
  'request_limit_raise',
  'turn_on',
  'go_live',
]);

export const TENANT_MODES = Object.freeze(['practice', 'live']);

export const NEEDS_YOU_CODES = Object.freeze([
  'menu_task_missing',
  'menu_task_off',
  'form_out_of_date',
  'form_paused',
  'source_failed',
  'source_taught_nothing',
]);

export const NEEDS_YOU_FIXES = Object.freeze([
  'edit_menu',
  'publish_form',
  'add_again',
  'replace_source',
]);

export const NEEDS_YOU_SUBJECTS = Object.freeze(['menu', 'task', 'source']);

export const INGEST_REASON_CODES = Object.freeze([
  'site_busy',
  'site_blocked',
  'site_unreachable',
  'timed_out',
  'file_unreadable',
  'too_large',
  'nothing_found',
  'paused_by_limits',
  'no_credits',
  'our_side',
  'unknown',
]);

export const INGEST_NOTE_CODES = Object.freeze([
  'nothing_found',
  'page_limit',
  'partial_crawl',
  'unknown',
]);

export const SETUP_REQUESTS = Object.freeze([
  'assistant',
  'broadcasts',
  'limit_raise',
]);

/**
 * How many test numbers a practising account may keep: the sidecar's `MAX_TESTER_PHONES`
 * (`ai-sidecar/src/status/testers.ts`). The status read does not carry it, and a practising
 * owner who is refused an eleventh number is told why by the editor, so a copy here is safe
 * in the one direction that matters: it can only be stricter than the server.
 */
export const READINESS_MAX_TESTERS = 10;

const STOPPED = new Set([
  'paused_by_us',
  'platform_limit',
  'out_of_credits',
  'month_limit',
  'day_limit',
]);

/**
 * The colour family of a state: `on` (the accent), `practice` (amber), `stopped` (ruby: it
 * was answering and now is not) or `off` (slate: nothing is broken, it is simply not on).
 */
export const toneOf = state => {
  if (state === 'answering') return 'on';
  if (state === 'practice') return 'practice';
  if (STOPPED.has(state)) return 'stopped';
  return 'off';
};

/** `WASLO.STATUS.OUT_OF_CREDITS`, the i18n block for one state. */
export const statusCopyKey = state =>
  `WASLO.STATUS.${String(state).toUpperCase()}`;
